import * as React from 'react';
import { cn } from '../../utils';

export interface InputProps extends Omit<
  React.ComponentProps<'input'>,
  'size'
> {
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  error?: boolean;
  /** @default "default" */
  size?: 'default' | 'sm';
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type,
      startAdornment,
      endAdornment,
      error,
      size = 'default',
      ...props
    },
    ref,
  ) => {
    const inputElement = (
      <input
        type={type}
        className={cn(
          'flex w-full rounded-xl border-2 border-border bg-muted font-body file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground transition-all duration-200 ease-spring enabled:hover:border-foreground/40 focus-visible:outline-none focus-visible:border-foreground focus-visible:shadow-hard disabled:cursor-not-allowed disabled:opacity-50',
          size === 'default' && 'h-12 px-4 py-2 text-base',
          size === 'sm' && 'h-9 px-3 py-1.5 text-sm',
          error &&
            'border-destructive focus-visible:border-destructive focus-visible:shadow-[4px_4px_0_hsl(var(--destructive))]',
          startAdornment && size === 'default' && 'pl-11',
          startAdornment && size === 'sm' && 'pl-9',
          endAdornment && size === 'default' && 'pr-11',
          endAdornment && size === 'sm' && 'pr-9',
          className,
        )}
        ref={ref}
        {...(type === 'number' && {
          onWheel: (e: React.WheelEvent<HTMLInputElement>) =>
            e.currentTarget.blur(),
        })}
        {...props}
      />
    );

    if (!startAdornment && !endAdornment) return inputElement;

    return (
      <div className="relative">
        {startAdornment && (
          <div
            className={cn(
              'pointer-events-none absolute inset-y-0 flex items-center text-muted-foreground',
              size === 'default' && 'left-3.5 [&>svg]:size-5',
              size === 'sm' && 'left-3 [&>svg]:size-4',
            )}
          >
            {startAdornment}
          </div>
        )}
        {inputElement}
        {endAdornment && (
          <div
            className={cn(
              'absolute inset-y-0 flex items-center text-muted-foreground',
              size === 'default' && 'right-3.5 [&>svg]:size-5',
              size === 'sm' && 'right-3 [&>svg]:size-4',
            )}
          >
            {endAdornment}
          </div>
        )}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
